import React, { useState, useEffect } from 'react';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INPUT_SX = {
  '& .MuiInputBase-root': { fontSize: '14px' },
} as const;

const INPUT_STYLE = { padding: '4px 8px', fontSize: '14px' } as const;

interface Props {
  label: string;
  value: string[];
  onChange: (emails: string[]) => void;
  options: string[];
  onPendingValidityChange?: (isValid: boolean) => void;
}

export const EmailChipInput: React.FC<Props> = ({
  label,
  value,
  onChange,
  options,
  onPendingValidityChange,
}) => {
  const [inputValue, setInputValue] = useState('');

  const isInputValid = !inputValue || EMAIL_REGEX.test(inputValue.trim());

  useEffect(() => {
    onPendingValidityChange?.(isInputValid);
  }, [isInputValid, onPendingValidityChange]);

  const addEmail = (email: string) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !EMAIL_REGEX.test(trimmed) || value.includes(trimmed))
      return;
    onChange([...value, trimmed]);
    setInputValue('');
  };

  const removeEmail = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const filteredOptions = options;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '52px 1fr',
        columnGap: '12px',
        mb: 1.5,
      }}
    >
      <Typography variant='body2' sx={{ whiteSpace: 'nowrap', mt: '6px' }}>
        {label}
      </Typography>
      <Box style={{ width: '100%' }}>
        <Autocomplete
          freeSolo
          disableClearable
          options={filteredOptions}
          inputValue={inputValue}
          onInputChange={(_, v, reason) => {
            if (reason === 'input') setInputValue(v);
          }}
          onChange={(_, v) => {
            if (v && typeof v === 'string') addEmail(v);
          }}
          style={{ width: '100%' }}
          getOptionDisabled={(option) =>
            value.includes((option as string).toLowerCase())
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size='small'
              variant='outlined'
              error={!isInputValid}
              style={{ width: '100%' }}
              inputProps={{
                ...params.inputProps,
                style: INPUT_STYLE,
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  params.inputProps.onBlur?.(e);
                  addEmail(inputValue);
                },
              }}
              InputProps={{
                ...params.InputProps,
                style: { padding: 0, fontSize: '14px' },
              }}
              sx={INPUT_SX}
            />
          )}
          componentsProps={{
            paper: {
              sx: {
                '& .MuiAutocomplete-option': { fontSize: '14px' },
                '& .MuiAutocomplete-option[aria-disabled="true"]': {
                  opacity: 1,
                  backgroundColor: 'rgba(0,0,0,0.08)',
                  cursor: 'default',
                },
              },
            },
          }}
        />
        {value.length > 0 && (
          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {value.map((email, i) => (
              <Chip
                key={i}
                label={email}
                onDelete={() => removeEmail(i)}
                size='small'
                sx={{ backgroundColor: '#e0e0e0' }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
